/**
 * auth.service.ts — Layer 3: Auth Domain
 *
 * All authentication and session-related API calls live here.
 * Components and hooks import ONLY from this file — never from
 * http.service or url.service directly.
 */

import { getRequest, postRequest, patchRequest } from './http.service';
import {
  URL_LOGIN,
  URL_SIGNUP,
  URL_VERIFY_EMAIL,
  URL_RESEND_VERIFICATION,
  URL_RESEND_INVITE,
  URL_REQUEST_RESET,
  URL_RESET_PASSWORD,
  URL_TOKEN_EMAIL,
  URL_VERIFY_2FA_SETUP,
  URL_ACCEPT_TOS,
  URL_TERMS_ACCEPT,
  URL_VERIFY_PASSWORD,
  URL_SESSION_HEARTBEAT,
  URL_SESSION_END,
  URL_AUTH_LOGOUT,
  URL_GET_USER,
  URL_CORE_LOGIC_AUTH,
  URL_ADMIN_IMPERSONATE,
  URL_ADMIN_IMPERSONATE_EXIT,
  URL_ENVIRONMENT,
} from './url.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LoginPayload {
  email: string;
  password: string;
}

export interface SignupPayload {
  firstName: string;
  lastName: string;
  email: string;
  abn: string;
  orgName: string;
  password: string;
  documentHash?: string;
  tosVersion: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
}

export interface Verify2FAPayload {
  email: string;
  verificationCode: string;
}

// ---------------------------------------------------------------------------
// Auth functions
// ---------------------------------------------------------------------------

/** Sign in — returns user + token on success */
export const login = (payload: LoginPayload) =>
  postRequest(URL_LOGIN, payload as Record<string, unknown>);

/** Create a new organisation account */
export const signup = (payload: SignupPayload) =>
  postRequest(URL_SIGNUP, payload as Record<string, unknown>);

/** Verify email address after signup */
export const verifyEmail = (token: string) =>
  postRequest(URL_VERIFY_EMAIL, { token });

/** Re-send the email verification link */
export const resendVerification = (email: string) =>
  postRequest(URL_RESEND_VERIFICATION, { email });

/** Re-send an invite email for an existing pending user */
export const resendInvite = (email: string) =>
  postRequest(URL_RESEND_INVITE, { email });

/** Trigger a password-reset email */
export const requestPasswordReset = (email: string) =>
  postRequest(URL_REQUEST_RESET, { email });

/** Complete the password reset with the emailed token */
export const resetPassword = (payload: ResetPasswordPayload) =>
  postRequest(URL_RESET_PASSWORD, payload as Record<string, unknown>);

/** Exchange a token sent via email for a session */
export const tokenEmail = (token: string) =>
  postRequest(URL_TOKEN_EMAIL, { token });

/** Verify a 2FA setup code */
export const verify2FASetup = (payload: Verify2FAPayload) =>
  postRequest(URL_VERIFY_2FA_SETUP, payload as Record<string, unknown>);

/** Verify the shared password protecting an e-sign document link */
export const verifyEsignPassword = (token: string, enteredPassword: string) =>
  postRequest(URL_VERIFY_PASSWORD, { token, enteredPassword });

/** Accept terms of service for the current user — returns a refreshed JWT */
export const acceptTOS = () =>
  patchRequest(URL_ACCEPT_TOS, {});

/** Record acceptance of a specific terms/privacy document (with hash + method) */
export const acceptTerms = (payload: Record<string, unknown> = {}) =>
  postRequest(URL_TERMS_ACCEPT, payload);

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

/** Keep the session alive (call on a timer) */
export const heartbeat = () =>
  postRequest(URL_SESSION_HEARTBEAT, {});

/** Explicitly end the session (logout) */
export const endSession = (payload: Record<string, unknown> = {}) =>
  postRequest(URL_SESSION_END, payload);

/** Invalidate the refresh token on logout */
export const logoutWithRefreshToken = (refreshToken: string) =>
  postRequest(URL_AUTH_LOGOUT, { refreshToken });

/** Look up the current user from a stored auth token (session bootstrap) */
export const fetchCurrentUser = (token: string) =>
  getRequest(URL_GET_USER(token));

/** Get a short-lived token for the core-logic (Python) service */
export const fetchCoreLogicAuthToken = () =>
  getRequest(URL_CORE_LOGIC_AUTH);

/** Get the running environment (local/development/uat/production) */
export const fetchEnvironment = () =>
  getRequest(URL_ENVIRONMENT);

// ---------------------------------------------------------------------------
// Admin impersonation
// ---------------------------------------------------------------------------

/** Start impersonating an organisation (global-admin only) */
export const impersonate = (targetOrgId: string) =>
  postRequest(URL_ADMIN_IMPERSONATE, { targetOrgId });

/** Exit impersonation and return to the admin account */
export const exitImpersonation = () =>
  postRequest(URL_ADMIN_IMPERSONATE_EXIT, {});
